// @flow

/**
 * @deprecated
 *
 * All canonical URLs and their routes should come from graphql and Url Service. You
 * should not need this module for building or parsing canonical URLs and it won't work
 * in most cases. There is legacy support for building canonical URL for social pages but they
 * will be removed in the future. Reach out to web platform and SEO if you think you have
 * a need to use this module.
 */

import { build as buildHome, parse as parseHome } from './Home';
import { build as buildProfile, parse as parseProfile } from './Profile';
import { build as buildTrips, parse as parseTrips } from './Trips';
import { build as buildSaves, parse as parseSaves } from './Saves';
import { buildTripModal } from './modals';

const ARGS_PATTERN = /^([^-]+)-(.*)$/;
const HTML_ENDING = /\.([a-z]*[_|.])?(?:html|xml)$/;
const FILTER_PREFIX = 'zf';
const UNIFIED_DATES_PREFIX = 'zu';
const RELATIVE_DATES_PREFIX = 'zr';
const SUPPLIER_PREFIX = 'zs';

const isUpper = (s: string): boolean => {
  if (s) {
    return s === s.toUpperCase() && s !== s.toLowerCase();
  }
  return false;
};

const isDigit = (s: string): boolean => !isNaN(parseInt(s, 10));

/**
 * Translated from UrlArg#getParamFullName. Currently only supports a small subset of parameters because
 * this is supposed to be a temporary fix for the current router to work for more servlets. If you need more
 * parameters copy them from UrlArg#m_hPARAM_NAMES.
 *
 * @param param short param name
 * @return full param name
 */
const getParamFullName = param => {
  switch (param) {
    case 'g':
      return 'geo';
    case 'd':
      return 'detail';
    default:
      return param;
  }
};

/**
 * Translated from part of UrlArg#parseDashDelimitedArgs and support a subset of its parameter formats.
 * This is supposed to be a temporary fix for the current router so that it works for more servlets.
 * Previously it only worked for detail review pages.
 *
 * @param args args string to parse.
 * @return an object where key is param name and value is param value.
 */
const parseDashDelimitedArgs = (args: string) => {
  const m = HTML_ENDING.exec(args);
  const argsWithoutHtml = m && m.index ? args.substring(0, m.index) : args;
  const tokens = argsWithoutHtml.split('-');

  return tokens
    .filter(token => token !== null && token !== '')
    .map(token => {
      let key = null;
      let value = null;
      if (
        token.length >= 3 &&
        (token.startsWith(FILTER_PREFIX) ||
          token.startsWith(UNIFIED_DATES_PREFIX) ||
          token.startsWith(RELATIVE_DATES_PREFIX) ||
          token.startsWith(SUPPLIER_PREFIX))
      ) {
        // check for extended filter arguments in form of z[furs]?
        key = token.substring(0, 3);
        value = token.substring(3);
      } else {
        // assume "first-letter-is-key" and "rest-is-value" format
        key = token.substring(0, 1);
        value = token.substring(1);
        // if sKey starts with an uppercase letter, ignore it.
        if (isUpper(key) || isDigit(key) || key === '_') {
          return null;
        }
        key = getParamFullName(key);
      }

      return { key, value };
    })
    .reduce((params, kv) => {
      if (kv && kv.key !== null && kv.key !== '' && kv.value !== null) {
        const kvParam = {};
        kvParam[kv.key] = kv.value;
        return { ...kvParam, ...params };
      }
      return params;
    }, {});
};

const parseCanonicalDefault = (page: string, ...pathParams: Array<string>) => {
  const canonicalMatch = ARGS_PATTERN.exec(page);
  let canonicalParams = {};
  if (canonicalMatch) {
    /*
      canonicalMatch[0] is the full url
      canonicalMatch[1] is the page name
      canonicalMatch[2] is the args string
     */
    canonicalParams = {
      page: canonicalMatch[1],
    };
    if (canonicalMatch[2]) {
      const params = parseDashDelimitedArgs(canonicalMatch[2]);
      canonicalParams = {
        ...params,
        ...canonicalParams,
      };
    }
  }

  const nonEmptyPathParams: ?Array<string> = pathParams
    ? pathParams.filter(par => par !== '')
    : undefined;
  if (nonEmptyPathParams && nonEmptyPathParams.length) {
    return { page, ...canonicalParams, nonEmptyPathParams };
  }

  return { page, ...canonicalParams };
};

// *** Please don't add more pages ***
export const build = {
  default: buildTripModal((page: string, params: any) => ({ path: page, ...params })),
  Home: buildHome,
  Profile: buildProfile,
  Trips: buildTrips,
  Saves: buildSaves,
};

// *** Please don't add more pages ***
export const parse = {
  default: parseCanonicalDefault,
  Home: parseHome,
  Profile: parseProfile,
  Trips: parseTrips,
  Saves: parseSaves,
};
